import { FormContextParts } from 'components/forms/form/Form.types';
import {
  CustomDataFieldType,
  CustomDataFieldUsage,
  UpdateCustomDataFieldsInternalPayload,
} from 'models/CustomDataField';
import useUpdateInternalCustomDataFields from 'components/domain/customData/fields/internal/useUpdateInternalCustomDataFields';
import { useTranslation } from 'react-i18next';
import useNotify from 'hooks/useNotify';
import { useQueryClient } from '@tanstack/react-query';
import { INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY } from 'components/domain/customData/fields/internal/useFetchInternalCustomDataFields';
import {
  PreArrivalFormFields,
  PreArrivalFormValues,
} from './PreArrivalFormTab.types';
import {
  FIELDS_WITH_MIN_AGE_TO_SHOW,
  MIN_AGE_TO_SHOW,
} from './PreArrivalFormTab.constants';

const usePreArrivalFormSubmit = () => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotify();
  const queryClient = useQueryClient();
  const { updateInternalCustomFields } = useUpdateInternalCustomDataFields();

  return async (
    formValues: PreArrivalFormValues,
    _: unknown,
    {
      formState: { dirtyFields },
      reset,
    }: FormContextParts<PreArrivalFormValues>,
  ) => {
    if (Object.keys(dirtyFields).length === 0) {
      return;
    }

    const payload: UpdateCustomDataFieldsInternalPayload = {
      customDataFields: [],
    };

    const formatMinAgeToShow = (minAgeToShow: number | null) => {
      if (minAgeToShow) {
        return MIN_AGE_TO_SHOW;
      }
      return null;
    };

    let shouldSetMinAgeToShowToNull = false;

    Object.keys(dirtyFields).forEach((key) => {
      if (key === 'preview') {
        return;
      }

      if (key === 'newCustomFields') {
        const newCustomFieldsValues = formValues.newCustomFields;

        Object.values(newCustomFieldsValues).forEach((field) => {
          if (field.type === CustomDataFieldType.SINGLE_SELECT) {
            payload.customDataFields.push({
              type: field.type,
              name: field.name,
              subType: field.subType,
              minAgeToShow: formatMinAgeToShow(field.minAgeToShow),
              isActive: field.isActive,
              isOptional: field.validation === 'OPTIONAL',
              // static values to represent the actual Boolean values,
              // in the future, we may need to treat it differently, depending on the implementation.
              options: [
                {
                  optionText: 'Yes',
                  optionValue: 'yes',
                  sortOrder: 1,
                  isActive: field.isActive,
                },
                {
                  optionText: 'No',
                  optionValue: 'no',
                  sortOrder: 2,
                  isActive: field.isActive,
                },
              ],
            });
            return;
          }

          payload.customDataFields.push({
            type: field.type,
            name: field.name,
            subType: field.subType,
            minAgeToShow: formatMinAgeToShow(field.minAgeToShow),
            isActive: field.isActive,
            isOptional: field.validation === 'OPTIONAL',
          });
        });

        return;
      }

      if (key === 'customFields') {
        const customFieldsValues = formValues.customFields;

        Object.values(customFieldsValues).forEach((field) => {
          payload.customDataFields.push({
            uid: field.uid!,
            // @ts-expect-error
            ...(FIELDS_WITH_MIN_AGE_TO_SHOW.includes(key) && {
              minAgeToShow: formatMinAgeToShow(field.minAgeToShow),
            }),
            isActive: field.isActive,
            isOptional: field.validation === 'OPTIONAL',
          });
        });

        return;
      }

      // if we are updating the other guest information age inactive/active, we need to make sure that:
      if (key === PreArrivalFormFields.OTHER_GUEST_AGE) {
        // if we are inactivating it, the min age to show for government id and email should now be null:
        if (formValues[key as PreArrivalFormFields]?.isActive === false) {
          shouldSetMinAgeToShowToNull = true;
        }
      }

      const value = formValues[key as PreArrivalFormFields];

      // already created field, we just need to update with the dirty fields.
      if (value.uid) {
        payload.customDataFields.push({
          uid: value.uid,
          // @ts-expect-error
          ...(FIELDS_WITH_MIN_AGE_TO_SHOW.includes(key) && {
            minAgeToShow: formatMinAgeToShow(value.minAgeToShow),
          }),
          isActive: value.isActive,
          isOptional: value.validation === 'OPTIONAL',
        });
      } else {
        payload.customDataFields.push({
          type: value.type,
          name: value.name,
          subType: value.subType,
          minAgeToShow: formatMinAgeToShow(value.minAgeToShow),
          isActive: value.isActive,
          isOptional: value.validation === 'OPTIONAL',
        });
      }
    });

    if (shouldSetMinAgeToShowToNull) {
      // find the other guest government id and other guest email and set the min age to show to null
      const otherGuestGovernmentIdUid =
        formValues.OTHER_GUEST_GOVERNMENT_ID.uid;
      const otherGuestEmailUid = formValues.OTHER_GUEST_EMAIL.uid;

      if (otherGuestGovernmentIdUid) {
        const otherGuestGovernmentIdIndex = payload.customDataFields.findIndex(
          (field) => field.uid === otherGuestGovernmentIdUid,
        );
        if (otherGuestGovernmentIdIndex !== -1) {
          payload.customDataFields[otherGuestGovernmentIdIndex].minAgeToShow =
            null;
        } else {
          payload.customDataFields.push({
            uid: otherGuestGovernmentIdUid,
            minAgeToShow: null,
          });
        }
      }

      if (otherGuestEmailUid) {
        const otherGuestEmailIndex = payload.customDataFields.findIndex(
          (field) => field.uid === otherGuestEmailUid,
        );
        if (otherGuestEmailIndex !== -1) {
          payload.customDataFields[otherGuestEmailIndex].minAgeToShow = null;
        } else {
          payload.customDataFields.push({
            uid: otherGuestEmailUid,
            minAgeToShow: null,
          });
        }
      }
    }

    await updateInternalCustomFields(payload)
      .then(() => {
        notifySuccess(t('pageAutomations.preArrivalForm.successSaving'));
      })
      .catch(() => {
        notifyError(t('pageAutomations.preArrivalForm.errorSaving'));
        reset();
      })
      .finally(() => {
        queryClient.invalidateQueries({
          queryKey: [
            INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY,
            CustomDataFieldUsage.PRE_ARRIVAL_FORM,
          ],
        });
      });
  };
};

export default usePreArrivalFormSubmit;
